import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import { DASHBOARD_PAGE_SIZE } from '../../../config/constants';
import { Select, Button, withParams } from '../../../components';
import { buildPatientProfilePicUrl } from '../../../lib/util';
import { short } from '../common/formatting/date';
import { getPracticePanel, getEndedCount, getEnded } from './api';

const FILTER_OPTIONS = [
	{ value: 'all', label: 'All Visits' },
	{ value: 'mine', label: 'My Visits' },
];
const STEP_SIZE = 5;
const PRACTICE_PATIENT_OPTIONS = [{ value: 'all', label: 'All Patients' }];

const patientSort = (patient1, patient2) => {
	if (patient1.patient_name_last.trim() < patient2.patient_name_last.trim()) {
		return -1;
	} else if (
		patient1.patient_name_last.trim() === patient2.patient_name_last.trim()
	) {
		if (
			patient1.patient_name_first.trim() < patient2.patient_name_first.trim()
		) {
			return -1;
		} else if (
			patient1.patient_name_first.trim() === patient2.patient_name_first.trim()
		) {
			return 0;
		} else {
			return 1;
		}
	} else {
		return 1;
	}
};

const Gender = ({ gender }) => (
	<React.Fragment>
		{gender && gender === 'M' && 'Male'}
		{gender && gender === 'F' && 'Female'}
	</React.Fragment>
);
Gender.propTypes = {
	gender: PropTypes.string,
};

const Row = ({ visit, isDependentVisit = false }) => {
	const { patient, patient_dependent = {} } = visit;
	let birthDttm = '<Not Set>';
	if (isDependentVisit && patient_dependent.dependent_birth_dttm) {
		birthDttm = short(patient_dependent.dependent_birth_dttm);
	} else if (patient.birth_dttm) {
		birthDttm = short(patient.birth_dttm);
	}
	const startDttm = short(visit.start_dttm);
	return (
		<tr>
			<td>
				<div className="media">
					{isDependentVisit ? (
						<div className="thumb32 bg-info img-circle dependent-icon">
							<em className="fa fa-child fa-inverse fa-2x"></em>
						</div>
					) : (
						<img
							src={buildPatientProfilePicUrl(patient)}
							alt="Image"
							className="img-responsive img-circle"
						/>
					)}
				</div>
			</td>
			<td>
				<Link to={`/evisit/${visit.id}`} className="fname">
					{isDependentVisit
						? `${patient_dependent.dependent_name} ${patient_dependent.dependent_name_last} (dependent of ${patient.name_first} ${patient.name_last})`
						: `${patient.name_last}, ${patient.name_first}`}
				</Link>
			</td>
			<td>
				{isDependentVisit ? (
					<Gender gender={patient_dependent.dependent_gender} />
				) : (
					<Gender gender={patient.gender} />
				)}
			</td>
			<td>{birthDttm}</td>
			<td>{startDttm}</td>
			<td>
				{visit.provider_visit_type && visit.provider_visit_type.visit_type}
			</td>
		</tr>
	);
};
Row.propTypes = {
	visit: PropTypes.object.isRequired,
	isDependentVisit: PropTypes.bool,
	onRowClick: PropTypes.func,
};

class PreviousVisits extends React.Component {
	static propTypes = {
		// page: PropTypes.string,
		user: PropTypes.object.isRequired,
		params: PropTypes.object,
	};

	// static defaultProps = { user: {} };

	constructor(props) {
		super(props);

		let page = 1;
		if (props.params?.page) {
			page = parseInt(props.params.page, 10);
		}

		this.state = {
			visits: [],
			filter: 'all',
			isLoading: false,
			isLoadingCount: false,
			page,
			totalCount: 0,
			patientOptions: [PRACTICE_PATIENT_OPTIONS[0]],
			filterByPatient: PRACTICE_PATIENT_OPTIONS[0].value,
		};
	}

	componentDidMount() {
		this.fetchData();
		this.fetchCount();
		this.fetchPracticePatients();
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.page !== this.state.page ||
			prevState.filter !== this.state.filter ||
			prevState.filterByPatient !== this.state.filterByPatient
		) {
			this.fetchData();
		}
		if (
			prevState.filter !== this.state.filter ||
			prevState.filterByPatient !== this.state.filterByPatient
		) {
			this.fetchCount();
		}
	}

	fetchData = () => {
		const { page, filter, filterByPatient } = this.state;
		this.setState({ isLoading: true });
		const providerId =
			filter === FILTER_OPTIONS[0].value
				? undefined
				: this.props.user.get('id');
		const patientId =
			filterByPatient === PRACTICE_PATIENT_OPTIONS[0].value
				? undefined
				: filterByPatient;
		getEnded({
			providerPracticeId: this.props.user.get('provider_practice_id'),
			providerId,
			patientId,
			page,
		}).then(({ data }) => {
			this.setState({
				isLoading: false,
				visits: data,
			});
		});
	};

	fetchCount = () => {
		const { filter, filterByPatient } = this.state;
		this.setState({ isLoadingCount: true });
		const providerId =
			filter === FILTER_OPTIONS[0].value
				? undefined
				: this.props.user.get('id');
		const patientId =
			filterByPatient === PRACTICE_PATIENT_OPTIONS[0].value
				? undefined
				: filterByPatient;
		getEndedCount({
			providerPracticeId: this.props.user.get('provider_practice_id'),
			providerId,
			patientId,
		}).then(({ data }) => {
			this.setState({
				totalCount: data.length,
				isLoadingCount: false,
			});
		});
	};

	fetchPracticePatients = () => {
		getPracticePanel(this.props.user.get('provider_practice_id'))
			.then(({ status }) => {
				const { practicePanel } = status;
				// remove non-unique patients
				const uniques = {};
				practicePanel.forEach((patient) => {
					uniques[patient.patientId] = patient;
				});
				// sort last name, first name
				const data = Object.keys(uniques).map(
					(patientId) => uniques[patientId]
				);
				this.setState({
					patientOptions: [
						PRACTICE_PATIENT_OPTIONS[0],
						...data
							.sort(patientSort)
							.map(({ patientId, patient_name_first, patient_name_last }) => ({
								value: patientId,
								label: `${patient_name_last}, ${patient_name_first}`,
							})),
					],
				});
			})
			.catch((/* e */) => {
				// console.log(e);
			});
	};

	handleFilterByPatientChange = ({ target }) => {
		this.setState({ filterByPatient: target.value });
	};

	handleFilterChange = ({ target }) => {
		this.setState({ filter: target.value });
	};

	handlePreviousClick = () => {
		this.setState(({ page }) => {
			const prevPage = page - 1;
			window.location = `#previousevisits/${prevPage}`;
			return { page: prevPage };
		});
	};

	handlePreviousStepClick = () => {
		this.setState(({ page }) => {
			let prevPage = page - STEP_SIZE;
			if (prevPage < 1) {
				prevPage = 1;
			}
			window.location = `#previousevisits/${prevPage}`;
			return { page: prevPage };
		});
	};

	handleNextClick = () => {
		this.setState(({ page }) => {
			const nextPage = page + 1;
			window.location = `#previousevisits/${nextPage}`;
			return { page: nextPage };
		});
	};

	handleNextStepClick = () => {
		this.setState(({ page, totalCount }) => {
			let nextPage = page + STEP_SIZE;
			const maxPage = Math.ceil(totalCount / DASHBOARD_PAGE_SIZE);
			if (nextPage > maxPage) {
				nextPage = maxPage;
			}
			window.location = `#previousevisits/${nextPage}`;
			return { page: nextPage };
		});
	};

	render() {
		const {
			filter,
			isLoading,
			page,
			totalCount,
			visits,
			isLoadingCount,
			patientOptions,
			filterByPatient,
		} = this.state;

		let endingAt = page * DASHBOARD_PAGE_SIZE;
		const startingAt = endingAt - DASHBOARD_PAGE_SIZE + 1;
		const isLoadingData = isLoading || isLoadingCount;

		if (endingAt > totalCount) {
			endingAt = startingAt + visits.length - 1;
		}

		return (
			<div className="content-wrapper previous-visits">
				<div
					className={cn('panel', 'panel-default', {
						whirl: isLoading,
						traditional: isLoading,
					})}
				>
					<div className="panel-heading">
						<div className="row">
							<div className="col-lg-12">
								<div className="pull-right">
									<Select
										selectedValue={filter}
										options={FILTER_OPTIONS}
										onChange={this.handleFilterChange}
									/>
								</div>
								<div className="pull-left">
									<Select
										selectedValue={filterByPatient}
										options={patientOptions}
										onChange={this.handleFilterByPatientChange}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="table-responsive">
						<table className="table table-bordered table-hover">
							<thead>
								<tr>
									<th></th>
									<th>Last Name, First Name</th>
									<th>Gender</th>
									<th>DOB</th>
									<th>Visit Date</th>
									<th>Reason for Visit</th>
								</tr>
							</thead>
							<tbody>
								{visits.map((visit) => (
									<Row
										key={visit.id}
										visit={visit}
										isDependentVisit={Boolean(visit.patientDependentId)}
									/>
								))}
								{visits.length === 0 && !isLoading && (
									<tr>
										<td colSpan={6} className="text-center">
											No Previous Visits found
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<div className="panel-footer">
						<div className="row" style={{ display: 'flex' }}>
							<div className="col-lg-2" style={{ margin: 'auto' }}>
								{startingAt} - {endingAt} of {totalCount}
							</div>
							<div className="col-lg-8 text-center" style={{ margin: 'auto' }}>
								{isLoadingCount && (
									<em className="fa fa-spin fa-circle-o-notch" />
								)}
							</div>
							<div className="col-lg-2">
								<div className="pull-right">
									<div className="btn-group">
										<Button
											onClick={this.handlePreviousClick}
											disabled={startingAt === 1 || isLoadingData}
											icon="angle-left"
										/>
										<Button
											onClick={this.handlePreviousStepClick}
											disabled={startingAt === 1 || isLoadingData}
											icon="angle-double-left"
										/>
										<Button
											onClick={this.handleNextStepClick}
											disabled={isLoadingData || totalCount === endingAt}
											icon="angle-double-right"
										/>
										<Button
											onClick={this.handleNextClick}
											icon="angle-right"
											disabled={isLoadingData || totalCount === endingAt}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default withParams(PreviousVisits);
